import { useState, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useApiClient } from '@rudi/api/react';
import { ArrowLeft, ChevronRight, Download, MapPin, MessagesSquare, RefreshCw } from 'lucide-react';

// Chat's trace API, reached through the Cortex backend (`/api/chat-traces`).
const endpoint = '/chat-traces';
const PAGE_SIZE = 50;

type Location = {
  city: string | null;
  region: string | null;
  zip: string | null;
  lat: number | null;
  lon: number | null;
};
type ChatSummary = {
  id: string;
  title: string | null;
  created_at: string;
  source: string | null;
  tenant: string | null;
  location: Location;
  message_count: number;
  turns: number;
  tool_calls: number;
  input_tokens: number;
  output_tokens: number;
  estimated_cost_usd: number | null;
  failed_turns: number;
  model: string | null;
};
type Span = {
  id: string;
  seq: number;
  type: 'generation' | 'tool';
  name: string;
  model: string | null;
  round: number | null;
  duration_ms: number | null;
  input_tokens: number | null;
  output_tokens: number | null;
  input: unknown;
  output: unknown;
  metadata: Record<string, unknown>;
};
type Trace = {
  id: string;
  user_message_id: string | null;
  assistant_message_id: string | null;
  source: string | null;
  campaign: string | null;
  provider: string;
  model: string;
  route_reason: string;
  status: string;
  refused: boolean;
  error: string | null;
  started_at: string;
  time_to_first_token_ms: number | null;
  latency_ms: number | null;
  input_tokens: number | null;
  output_tokens: number | null;
  estimated_cost_usd: number | null;
  estimated_cost_eur: number | null;
  model_rounds: number;
  tool_call_count: number;
  prompt_sha256: string | null;
  app_version: string | null;
  input: { content?: string; attachments?: Record<string, unknown>[] };
  output: string | null;
  context_message_count: number;
  metadata: Record<string, unknown>;
  spans: Span[];
};
type ChatMessage = {
  id: string;
  role: 'user' | 'assistant' | 'system';
  content: string;
  created_at: string;
  order: number;
  metadata: Record<string, unknown> | null;
};
type ChatEvent = {
  id: string;
  name: string;
  created_at: string;
  source: string | null;
  metadata: Record<string, unknown>;
};
type ChatDetail = {
  id: string;
  title: string | null;
  created_at: string;
  source: string | null;
  tenant: string | null;
  location: Location;
  messages: ChatMessage[];
  traces: Trace[];
  events: ChatEvent[];
};
type SourceStats = {
  source: string | null;
  scans: number;
  return_visits: number;
  chats: number;
  turns: number;
  emails_sent: number;
};
type Stats = {
  started_after: string;
  started_before: string;
  totals: {
    turns: number;
    completed: number;
    errors: number;
    aborted: number;
    refused: number;
    input_tokens: number;
    output_tokens: number;
    estimated_cost_usd: number;
    estimated_cost_eur: number;
    latency_ms_p50: number | null;
    latency_ms_p95: number | null;
    time_to_first_token_ms_p50: number | null;
  };
  sources: SourceStats[];
  days: { day: string; chats: number; turns: number }[];
  tools: { name: string; calls: number; avg_duration_ms: number | null }[];
};

const control = 'rounded-3xl border bg-background px-3 py-2 text-sm';
const panel = 'rounded-3xl border bg-card p-5';
const dateText = (value: string) => new Date(value).toLocaleString();
const ms = (value: number | null) => (value == null ? '–' : `${value.toLocaleString()} ms`);
const usd = (value: number | null) => (value == null ? 'Unknown' : `$${Number(value).toFixed(4)}`);
const place = (location: Location) =>
  [location.zip, location.city, location.region].filter(Boolean).join(' ') || 'Unknown';

function errorText(error: unknown): string {
  const detail = (error as { response?: { data?: { detail?: unknown } } })?.response?.data?.detail;
  if (typeof detail === 'string') return detail;
  return error instanceof Error ? error.message : 'The request failed. Try again.';
}

function ErrorNotice({ error }: { error: unknown }) {
  return error ? (
    <p role="alert" className="text-sm text-destructive">
      {errorText(error)}
    </p>
  ) : null;
}

function Json({ value }: { value: unknown }) {
  return (
    <pre className="max-h-96 overflow-auto whitespace-pre-wrap break-words rounded-3xl bg-muted p-4 text-xs">
      {typeof value === 'string' ? value : JSON.stringify(value, null, 2)}
    </pre>
  );
}

function Fact({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div>
      <dt className="text-xs text-muted-foreground">{label}</dt>
      <dd className="text-sm">{children}</dd>
    </div>
  );
}

function toParams(form: HTMLFormElement): Record<string, string> {
  return Object.fromEntries(
    [...new FormData(form).entries()]
      .filter(([, value]) => value !== '')
      .map(([key, value]) => [
        key,
        key.startsWith('started_') ? new Date(String(value)).toISOString() : String(value),
      ])
  );
}

export function ChatTracesPage() {
  const [selected, setSelected] = useState<string | null>(null);
  const [tab, setTab] = useState<'chats' | 'sources'>('chats');
  if (selected) return <ChatTraceDetail id={selected} onBack={() => setSelected(null)} />;
  return (
    <div className="space-y-6" data-testid="chat-traces-page">
      <div>
        <h1 className="text-2xl font-semibold">Chat traces</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Patient conversations from Chat, with the model, tool calls, campaign source and location
          of every answer.
        </p>
      </div>
      <div className="flex flex-wrap gap-2" role="tablist" aria-label="Chat trace views">
        {(['chats', 'sources'] as const).map(value => (
          <button
            key={value}
            role="tab"
            aria-selected={tab === value}
            className={`${control} ${tab === value ? 'bg-primary text-primary-foreground' : ''}`}
            onClick={() => setTab(value)}
          >
            {value === 'chats' ? 'Chats' : 'Sources and usage'}
          </button>
        ))}
      </div>
      {tab === 'chats' ? <ChatList onSelect={setSelected} /> : <SourcesView />}
    </div>
  );
}

function ChatList({ onSelect }: { onSelect: (id: string) => void }) {
  const api = useApiClient();
  const [filters, setFilters] = useState<Record<string, string>>({});
  const [offset, setOffset] = useState(0);
  const chats = useQuery({
    queryKey: ['chat-traces', filters, offset],
    queryFn: async () =>
      (
        await api.get<{ items: ChatSummary[]; total: number }>(`${endpoint}/chats`, {
          params: { ...filters, offset, limit: PAGE_SIZE },
        })
      ).data,
  });
  return (
    <>
      <form
        className={`${panel} grid gap-3 sm:grid-cols-3 lg:grid-cols-4`}
        onSubmit={event => {
          event.preventDefault();
          setFilters(toParams(event.currentTarget));
          setOffset(0);
        }}
      >
        {[
          ['source', 'Campaign source'],
          ['q', 'Text search'],
          ['city', 'City'],
          ['region', 'Region'],
          ['tool', 'Tool'],
          ['model', 'Model'],
          ['tenant', 'Chatbot'],
        ].map(([name, text]) => (
          <label key={name} className="grid gap-1 text-xs">
            {text}
            <input className={control} name={name} defaultValue={filters[name] ?? ''} />
          </label>
        ))}
        <label className="grid gap-1 text-xs">
          Status
          <select className={control} name="status" aria-label="Status" defaultValue="">
            <option value="">All turns</option>
            <option value="completed">Completed</option>
            <option value="error">Error</option>
            <option value="aborted">Aborted</option>
          </select>
        </label>
        <label className="grid gap-1 text-xs">
          From
          <input className={control} type="datetime-local" name="started_after" />
        </label>
        <label className="grid gap-1 text-xs">
          Until
          <input className={control} type="datetime-local" name="started_before" />
        </label>
        <button className={`${control} self-end bg-primary text-primary-foreground`} type="submit">
          Apply filters
        </button>
        <button
          type="button"
          className={`${control} self-end`}
          aria-label="Refresh chats"
          onClick={() => void chats.refetch()}
        >
          <RefreshCw className="mx-auto h-4 w-4" />
        </button>
      </form>
      <div className={panel}>
        <ErrorNotice error={chats.error} />
        {chats.isPending ? (
          <p>Loading chats…</p>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full text-left text-sm">
                <thead className="text-xs text-muted-foreground">
                  <tr>
                    {['Chat', 'Started', 'Source', 'Location', 'Turns', 'Tool calls', 'Cost'].map(
                      value => (
                        <th className="p-3" key={value}>
                          {value}
                        </th>
                      )
                    )}
                  </tr>
                </thead>
                <tbody>
                  {chats.data?.items.map(chat => (
                    <tr key={chat.id} className="border-t">
                      <td className="p-3">
                        <button
                          className="flex items-center gap-2 text-left font-medium hover:underline"
                          onClick={() => onSelect(chat.id)}
                        >
                          <MessagesSquare className="h-4 w-4 shrink-0" />
                          {chat.title || 'Untitled chat'}
                          <ChevronRight className="h-3 w-3" />
                        </button>
                        <p className="mt-1 text-xs text-muted-foreground">
                          {chat.message_count} messages · {chat.tenant ?? 'all'}
                          {chat.model ? ` · ${chat.model}` : ''}
                        </p>
                      </td>
                      <td className="p-3">{dateText(chat.created_at)}</td>
                      <td className="p-3">{chat.source ?? '–'}</td>
                      <td className="p-3">{place(chat.location)}</td>
                      <td className="p-3">
                        {chat.turns}
                        {chat.failed_turns > 0 && (
                          <p className="text-xs text-destructive">{chat.failed_turns} failed</p>
                        )}
                      </td>
                      <td className="p-3">{chat.tool_calls}</td>
                      <td className="p-3">{usd(chat.estimated_cost_usd)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {chats.data?.items.length === 0 && (
              <p className="py-10 text-center text-muted-foreground">
                No chats match these filters.
              </p>
            )}
            <div className="mt-4 flex items-center justify-between text-sm">
              <span>{chats.data?.total ?? 0} chats</span>
              <div className="flex gap-2">
                <button
                  className={control}
                  disabled={offset === 0}
                  onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}
                >
                  Previous
                </button>
                <button
                  className={control}
                  disabled={offset + PAGE_SIZE >= (chats.data?.total ?? 0)}
                  onClick={() => setOffset(offset + PAGE_SIZE)}
                >
                  Next
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </>
  );
}

function SourcesView() {
  const api = useApiClient();
  const [range, setRange] = useState<Record<string, string>>({});
  const [exportError, setExportError] = useState<unknown>(null);
  const stats = useQuery({
    queryKey: ['chat-trace-stats', range],
    queryFn: async () => (await api.get<Stats>(`${endpoint}/stats`, { params: range })).data,
  });
  async function download() {
    setExportError(null);
    try {
      const response = await api.get<Blob>(`${endpoint}/export`, {
        params: range,
        responseType: 'blob',
      });
      const url = URL.createObjectURL(response.data);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'chat-turns.jsonl';
      link.click();
      URL.revokeObjectURL(url);
    } catch (error) {
      setExportError(error);
    }
  }
  const totals = stats.data?.totals;
  return (
    <>
      <form
        className={`${panel} flex flex-wrap items-end gap-3`}
        onSubmit={event => {
          event.preventDefault();
          setRange(toParams(event.currentTarget));
        }}
      >
        <label className="grid gap-1 text-xs">
          From
          <input className={control} type="datetime-local" name="started_after" />
        </label>
        <label className="grid gap-1 text-xs">
          Until
          <input className={control} type="datetime-local" name="started_before" />
        </label>
        <button className={`${control} bg-primary text-primary-foreground`} type="submit">
          Apply
        </button>
        <button type="button" className={`${control} flex items-center gap-2`} onClick={download}>
          <Download className="h-4 w-4" />
          Export turns (JSON Lines)
        </button>
        <p className="text-xs text-muted-foreground">Without dates: the last 30 days.</p>
      </form>
      <ErrorNotice error={stats.error ?? exportError} />
      {stats.isPending && <p>Loading statistics…</p>}
      {totals && (
        <dl className={`${panel} grid gap-4 sm:grid-cols-3 lg:grid-cols-6`}>
          <Fact label="Turns">
            {totals.turns} ({totals.errors} errors, {totals.aborted} aborted, {totals.refused}{' '}
            refused)
          </Fact>
          <Fact label="Latency p50 / p95">
            {ms(totals.latency_ms_p50)} / {ms(totals.latency_ms_p95)}
          </Fact>
          <Fact label="First token p50">{ms(totals.time_to_first_token_ms_p50)}</Fact>
          <Fact label="Tokens in / out">
            {totals.input_tokens.toLocaleString()} / {totals.output_tokens.toLocaleString()}
          </Fact>
          <Fact label="Estimated cost">
            {usd(totals.estimated_cost_usd)}
            {Number(totals.estimated_cost_eur) > 0 &&
              ` + €${Number(totals.estimated_cost_eur).toFixed(4)}`}
          </Fact>
          <Fact label="Window">
            {new Date(stats.data!.started_after).toLocaleDateString()} –{' '}
            {new Date(stats.data!.started_before).toLocaleDateString()}
          </Fact>
        </dl>
      )}
      {stats.data && (
        <div className={panel}>
          <h2 className="mb-1 font-semibold">Campaign sources</h2>
          <p className="mb-3 text-xs text-muted-foreground">
            Scans come from the ?source= link itself; return visits reuse a source the browser
            stored earlier. Page views from before this was recorded count as scans.
          </p>
          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm">
              <thead className="text-xs text-muted-foreground">
                <tr>
                  {['Source', 'Scans', 'Return visits', 'Chats', 'Turns', 'Emails sent'].map(
                    value => (
                      <th className="p-2" key={value}>
                        {value}
                      </th>
                    )
                  )}
                </tr>
              </thead>
              <tbody>
                {stats.data.sources.map(row => (
                  <tr className="border-t" key={row.source ?? ''}>
                    <td className="p-2">{row.source ?? 'No source'}</td>
                    <td className="p-2">{row.scans}</td>
                    <td className="p-2">{row.return_visits}</td>
                    <td className="p-2">{row.chats}</td>
                    <td className="p-2">{row.turns}</td>
                    <td className="p-2">{row.emails_sent}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
      {stats.data && (
        <div className="grid gap-6 lg:grid-cols-2">
          <div className={panel}>
            <h2 className="mb-3 font-semibold">Tools</h2>
            <table className="w-full text-left text-sm">
              <tbody>
                {stats.data.tools.map(tool => (
                  <tr className="border-t" key={tool.name}>
                    <td className="p-2 font-mono text-xs">{tool.name}</td>
                    <td className="p-2">{tool.calls} calls</td>
                    <td className="p-2">{ms(tool.avg_duration_ms)} on average</td>
                  </tr>
                ))}
              </tbody>
            </table>
            {stats.data.tools.length === 0 && <p className="text-sm">No tool calls.</p>}
          </div>
          <div className={panel}>
            <h2 className="mb-3 font-semibold">Per day</h2>
            <table className="w-full text-left text-sm">
              <tbody>
                {stats.data.days.map(day => (
                  <tr className="border-t" key={day.day}>
                    <td className="p-2">{day.day}</td>
                    <td className="p-2">{day.chats} chats</td>
                    <td className="p-2">{day.turns} turns</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </>
  );
}

function ChatTraceDetail({ id, onBack }: { id: string; onBack: () => void }) {
  const api = useApiClient();
  const detail = useQuery({
    queryKey: ['chat-trace', id],
    queryFn: async () => (await api.get<ChatDetail>(`${endpoint}/chats/${id}`)).data,
  });
  const chat = detail.data;
  const byAssistant = new Map(
    (chat?.traces ?? []).filter(t => t.assistant_message_id).map(t => [t.assistant_message_id, t])
  );
  const shown = new Set(byAssistant.values());
  const unmatched = (chat?.traces ?? []).filter(trace => !shown.has(trace));
  const { lat, lon } = chat?.location ?? { lat: null, lon: null };
  return (
    <div className="space-y-6" data-testid="chat-trace-detail">
      <button className={`${control} flex items-center gap-2`} onClick={onBack}>
        <ArrowLeft className="h-4 w-4" />
        All chats
      </button>
      <ErrorNotice error={detail.error} />
      {detail.isPending && <p>Loading chat…</p>}
      {chat && (
        <>
          <div className={panel}>
            <h1 className="text-xl font-semibold">{chat.title || 'Untitled chat'}</h1>
            <dl className="mt-4 grid gap-4 sm:grid-cols-3 lg:grid-cols-6">
              <Fact label="Started">{dateText(chat.created_at)}</Fact>
              <Fact label="Campaign source">{chat.source ?? '–'}</Fact>
              <Fact label="Chatbot">{chat.tenant ?? 'all'}</Fact>
              <Fact label="Location">
                {lat != null && lon != null ? (
                  <a
                    className="flex items-center gap-1 hover:underline"
                    href={`https://www.openstreetmap.org/?mlat=${lat}&mlon=${lon}#map=11/${lat}/${lon}`}
                    target="_blank"
                    rel="noreferrer"
                  >
                    <MapPin className="h-3 w-3" />
                    {place(chat.location)}
                  </a>
                ) : (
                  place(chat.location)
                )}
              </Fact>
              <Fact label="Turns">{chat.traces.length}</Fact>
              <Fact label="Estimated cost">
                {usd(
                  chat.traces.some(t => t.estimated_cost_usd != null)
                    ? chat.traces.reduce((sum, t) => sum + Number(t.estimated_cost_usd ?? 0), 0)
                    : null
                )}
              </Fact>
            </dl>
          </div>
          <div className="space-y-4">
            {chat.messages.map(message => (
              <div key={message.id} className="space-y-2">
                <div
                  className={`${panel} ${message.role === 'user' ? 'bg-muted/40' : ''}`}
                  data-testid={`message-${message.role}`}
                >
                  <p className="mb-2 text-xs text-muted-foreground">
                    {message.role === 'user' ? 'Patient' : 'Assistant'} ·{' '}
                    {dateText(message.created_at)}
                    {message.metadata?.is_from_stt === true && ' · dictated'}
                  </p>
                  <p className="whitespace-pre-wrap text-sm">{message.content}</p>
                </div>
                {message.role === 'assistant' &&
                  (byAssistant.has(message.id) ? (
                    <TurnPanel trace={byAssistant.get(message.id)!} />
                  ) : (
                    <LegacyToolCalls metadata={message.metadata} />
                  ))}
              </div>
            ))}
          </div>
          {unmatched.length > 0 && (
            <div className="space-y-3">
              <h2 className="font-semibold">Turns without a saved answer</h2>
              {unmatched.map(trace => (
                <TurnPanel key={trace.id} trace={trace} />
              ))}
            </div>
          )}
          <div className={panel}>
            <h2 className="mb-3 font-semibold">Events</h2>
            {chat.events.length === 0 && <p className="text-sm">No events.</p>}
            {chat.events.map(event => (
              <details key={event.id} className="border-t py-2 text-sm">
                <summary className="cursor-pointer">
                  {event.name} · {dateText(event.created_at)}
                  {event.source ? ` · ${event.source}` : ''}
                </summary>
                <Json value={event.metadata} />
              </details>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

function TurnPanel({ trace }: { trace: Trace }) {
  const tools = trace.spans.filter(span => span.type === 'tool');
  const rounds = trace.spans.filter(span => span.type === 'generation');
  const [prompt, setPrompt] = useState(false);
  return (
    <div className="ml-6 space-y-3 rounded-3xl border border-dashed p-4" data-testid="turn-panel">
      <dl className="grid gap-3 sm:grid-cols-3 lg:grid-cols-6">
        <Fact label="Status">
          <span className={trace.status === 'completed' ? '' : 'text-destructive'}>
            {trace.status}
            {trace.refused ? ' (refused)' : ''}
          </span>
        </Fact>
        <Fact label="Model">
          {trace.model}
          <span className="block text-xs text-muted-foreground">{trace.route_reason}</span>
        </Fact>
        <Fact label="First token / total">
          {ms(trace.time_to_first_token_ms)} / {ms(trace.latency_ms)}
        </Fact>
        <Fact label="Tokens in / out">
          {trace.input_tokens ?? '?'} / {trace.output_tokens ?? '?'}
        </Fact>
        <Fact label="Estimated cost">
          {trace.estimated_cost_eur != null
            ? `€${Number(trace.estimated_cost_eur).toFixed(4)}`
            : usd(trace.estimated_cost_usd)}
        </Fact>
        <Fact label="Rounds / tool calls">
          {trace.model_rounds} / {trace.tool_call_count}
        </Fact>
      </dl>
      {trace.error && <p className="text-sm text-destructive">{trace.error}</p>}
      {tools.map(tool => (
        <details key={tool.id} className="rounded-3xl border p-3 text-sm">
          <summary className="cursor-pointer">
            <span className="font-mono">{tool.name}</span>
            {tool.round != null && ` · round ${tool.round}`} · {ms(tool.duration_ms)}
          </summary>
          <p className="mt-2 text-xs text-muted-foreground">Arguments</p>
          <Json value={tool.input} />
          <p className="mt-2 text-xs text-muted-foreground">Result</p>
          <Json value={tool.output} />
        </details>
      ))}
      {rounds.length > 0 && (
        <details className="text-sm">
          <summary className="cursor-pointer">Model rounds</summary>
          <table className="mt-2 w-full text-left text-xs">
            <tbody>
              {rounds.map(round => (
                <tr className="border-t" key={round.id}>
                  <td className="p-2">Round {round.round}</td>
                  <td className="p-2">{round.model}</td>
                  <td className="p-2">
                    {round.input_tokens ?? '?'} in / {round.output_tokens ?? '?'} out
                  </td>
                  <td className="p-2">{ms(round.duration_ms)}</td>
                  <td className="p-2">{String(round.metadata.stop_reason ?? '')}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </details>
      )}
      <details className="text-sm">
        <summary className="cursor-pointer">Turn details</summary>
        <Json
          value={{
            trace_id: trace.id,
            started_at: trace.started_at,
            campaign: trace.campaign,
            context_messages: trace.context_message_count,
            attachments: trace.input.attachments,
            app_version: trace.app_version,
            ...trace.metadata,
          }}
        />
      </details>
      {trace.prompt_sha256 &&
        (prompt ? (
          <PromptText sha256={trace.prompt_sha256} />
        ) : (
          <button className={control} onClick={() => setPrompt(true)}>
            Show system prompt {trace.prompt_sha256.slice(0, 8)}
          </button>
        ))}
    </div>
  );
}

function PromptText({ sha256 }: { sha256: string }) {
  const api = useApiClient();
  const prompt = useQuery({
    queryKey: ['chat-prompt', sha256],
    queryFn: async () =>
      (await api.get<{ text: string; tenant: string }>(`${endpoint}/prompts/${sha256}`)).data,
  });
  if (prompt.error) return <ErrorNotice error={prompt.error} />;
  return prompt.data ? <Json value={prompt.data.text} /> : <p className="text-sm">Loading…</p>;
}

/** Chats saved before server-side traces kept tool calls in the message metadata only. */
function LegacyToolCalls({ metadata }: { metadata: Record<string, unknown> | null }) {
  const calls = Array.isArray(metadata?.tool_calls)
    ? (metadata.tool_calls as { function?: string; arguments?: unknown; result?: unknown }[])
    : [];
  if (calls.length === 0) return null;
  return (
    <div className="ml-6 space-y-2 rounded-3xl border border-dashed p-4 text-sm">
      <p className="text-xs text-muted-foreground">
        Saved before server-side traces: tool calls only.
      </p>
      {calls.map((call, index) => (
        <details key={index} className="rounded-3xl border p-3">
          <summary className="cursor-pointer font-mono">{call.function ?? 'tool'}</summary>
          <Json value={call.arguments} />
          <Json value={call.result} />
        </details>
      ))}
    </div>
  );
}
